import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUsers, FaCoins, FaServer, FaMemory, FaUserShield, FaTimes, FaCheck, FaHdd, FaFlask, FaArrowRight, FaArrowUp, FaArrowDown, FaPlay, FaClipboardList, FaCalculator, FaCheckCircle, FaSearchPlus, FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import UserAdminDashboard from './UserAdminDashboard';

// Play icon mapping
const PLAY_ICONS = {
  'clipboard-list': FaClipboardList,
  'calculator': FaCalculator,
  'check-circle': FaCheckCircle,
  'search-check': FaSearchPlus,
};

const SystemAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [org, setOrg] = useState(null);
  const [activeTab, setActiveTab] = useState('system');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditMode, setCreditMode] = useState('add'); // 'add' or 'subtract'
  const [creditDescription, setCreditDescription] = useState('');
  const [licenseAmount, setLicenseAmount] = useState('');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [testData, setTestData] = useState({ enabled: false, test_sessions: 0, test_transactions: 0 });
  const [togglingTestData, setTogglingTestData] = useState(false);
  const [globalPlays, setGlobalPlays] = useState([]);
  const [togglingPlay, setTogglingPlay] = useState(null);

  // Event log download state
  const [logDays, setLogDays] = useState(1);
  const [logErrorsOnly, setLogErrorsOnly] = useState(false);
  const [logStats, setLogStats] = useState(null);
  const [downloadingLogs, setDownloadingLogs] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchOrg();
    fetchTestDataStatus();
    fetchGlobalPlays();
    fetchLogStats(1);
    const interval = setInterval(() => {
      fetchStats();
      fetchOrg();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch log stats when days selection changes
  useEffect(() => {
    fetchLogStats(logDays);
  }, [logDays]);

  const headers = {
    Authorization: `Bearer ${sessionStorage.getItem("userToken")}`,
    Accept: 'application/json'
  };

  const fetchStats = async () => {
    try {
      console.log('Fetching stats from:', `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/stats/`);
      const res = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/stats/`, { headers });
      console.log('Stats response:', res.data);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      console.error('Error response:', err.response?.data);
    }
  };

  const fetchOrg = async () => {
    try {
      console.log('Fetching orgs from:', `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/orgs/`);
      const res = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/orgs/`, { headers });
      console.log('Orgs response:', res.data);
      // Single org deployment - just take the first one
      if (res.data.length > 0) {
        setOrg(res.data[0]);
      }
    } catch (err) {
      console.error('Error fetching orgs:', err);
      console.error('Error response:', err.response?.data);
    }
  };

  const fetchTestDataStatus = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/test-data/`, { headers });
      setTestData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGlobalPlays = async () => {
    try {
      console.log('Fetching global plays from:', `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/plays/`);
      const res = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/plays/`, { headers });
      console.log('Global plays response:', res.data);
      setGlobalPlays(res.data.plays || []);
    } catch (err) {
      console.error('Error fetching global plays:', err);
      console.error('Error response:', err.response?.data);
    }
  };

  const handleToggleGlobalPlay = async (playId, currentEnabled) => {
    setTogglingPlay(playId);
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/plays/`,
        { play_id: playId, action: currentEnabled ? 'disable' : 'enable' },
        { headers }
      );
      toast.success(`Play ${currentEnabled ? 'disabled' : 'enabled'} globally`);
      fetchGlobalPlays();
    } catch (err) {
      toast.error('Failed to update play');
    } finally {
      setTogglingPlay(null);
    }
  };

  const handleToggleTestData = async () => {
    setTogglingTestData(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/test-data/`,
        { action: 'toggle' },
        { headers }
      );
      setTestData(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.message);
      fetchTestDataStatus();
    } catch (err) {
      toast.error('Failed to toggle test data');
    } finally {
      setTogglingTestData(false);
    }
  };

  const fetchLogStats = async (days = 1) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/event-logs/stats/?days=${days}`,
        { headers }
      );
      setLogStats(res.data);
    } catch (err) {
      console.error('Failed to fetch log stats:', err);
      setLogStats(null);
    }
  };

  const handleDownloadLogs = async () => {
    setDownloadingLogs(true);
    try {
      const params = new URLSearchParams({
        days: logDays,
        errors_only: logErrorsOnly,
        include_files: 'true',
      });

      const response = await axios.get(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/event-logs/download/?${params}`,
        {
          headers,
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `sourceoptima_logs_${today}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Logs downloaded successfully');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download logs');
    } finally {
      setDownloadingLogs(false);
    }
  };

  const handleAddCredits = async () => {
    if (!org || !creditAmount) return;
    const amount = creditMode === 'add' ? Math.abs(parseInt(creditAmount)) : -Math.abs(parseInt(creditAmount));
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/orgs/${org.id}/credits/`,
        { amount, description: creditDescription || `${creditMode === 'add' ? 'Credit addition' : 'Credit deduction'} by System Admin` },
        { headers }
      );
      toast.success(`${Math.abs(amount).toLocaleString()} credits ${creditMode === 'add' ? 'added' : 'removed'} successfully`);
      setShowCreditModal(false);
      setCreditAmount('');
      setCreditDescription('');
      setCreditMode('add');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update credits');
    }
  };

  const handleUpdateLicenses = async () => {
    if (!org || !licenseAmount) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/system/orgs/${org.id}/licenses/`,
        { licenses: parseInt(licenseAmount) },
        { headers }
      );
      toast.success('Licenses updated successfully');
      setShowLicenseModal(false);
      setLicenseAmount('');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update licenses');
    }
  };

  const openCreditModal = () => {
    setCreditAmount('');
    setCreditDescription('');
    setShowCreditModal(true);
  };

  const openLicenseModal = () => {
    setLicenseAmount(org?.max_concurrent_logins?.toString() || '5');
    setShowLicenseModal(true);
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">System Admin Dashboard</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('system')}
              className={`inline-block p-4 border-b-2 rounded-t-lg flex items-center gap-2 ${activeTab === 'system' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
              <FaServer /> System Stats
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('useradmin')}
              className={`inline-block p-4 border-b-2 rounded-t-lg flex items-center gap-2 ${activeTab === 'useradmin' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
              <FaUserShield /> User Admin View
            </button>
          </li>
        </ul>
      </div>

      {/* System Stats Tab */}
      {activeTab === 'system' && (
        <div>
          {/* Server Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded shadow flex items-center">
                <div className="p-3 bg-blue-100 text-blue-500 rounded-full mr-4">
                  <FaServer size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">CPU Usage</p>
                  <p className="text-xl font-bold">{stats.cpu_percent}%</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded shadow flex items-center">
                <div className="p-3 bg-purple-100 text-purple-500 rounded-full mr-4">
                  <FaMemory size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Memory</p>
                  <p className="text-xl font-bold">{stats.memory_percent}% ({stats.memory_used_gb} GB)</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded shadow flex items-center">
                <div className="p-3 bg-orange-100 text-orange-500 rounded-full mr-4">
                  <FaHdd size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Disk</p>
                  <p className="text-xl font-bold">{stats.disk_percent}% ({stats.disk_free_gb} GB free)</p>
                </div>
              </div>
              {org && (
                <div className="bg-white p-4 rounded shadow flex items-center">
                  <div className="p-3 bg-indigo-100 text-indigo-500 rounded-full mr-4">
                    <FaUsers size={24} />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Total Users</p>
                    <p className="text-xl font-bold">{org.user_count}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Credits & Licenses Management */}
          {org && (
            <div className="bg-white rounded shadow border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-700">Account Management</h2>
                <p className="text-xs text-gray-500">Manage credits and pooled licenses</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Credits Card */}
                  <div className="border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 text-green-600 rounded-full">
                          <FaCoins size={20} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Credit Balance</p>
                          <p className={`text-2xl font-bold ${org.credit_balance > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {org.credit_balance?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={openCreditModal}
                        className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition text-sm font-medium"
                      >
                        <FaCoins /> Manage
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Credits are consumed when running analysis plays</p>
                  </div>

                  {/* Licenses Card */}
                  <div className="border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-full">
                          <FaUsers size={20} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Pooled Licenses</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {org.max_concurrent_logins || 5}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={openLicenseModal}
                        className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition text-sm font-medium"
                      >
                        <FaUsers /> Manage
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">License threshold for tracking daily logins</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Global Play Management */}
          <div className="bg-white rounded shadow border border-gray-100 mt-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <FaPlay className="text-[#1e5045]" /> Global Play Settings
              </h2>
              <p className="text-xs text-gray-500">
                Enable or disable analysis plays system-wide. Disabled plays will not be visible to User Admins or users.
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {globalPlays.map(play => {
                  const IconComponent = PLAY_ICONS[play.icon] || FaPlay;
                  return (
                    <div
                      key={play.id}
                      className={`border rounded-lg p-4 transition-all ${
                        play.enabled
                          ? 'border-green-200 bg-green-50/50'
                          : 'border-gray-200 bg-gray-50/50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${
                            play.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'
                          }`}>
                            <IconComponent size={18} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{play.name}</p>
                            <p className="text-xs text-gray-500 mt-1">{play.description}</p>
                            <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                              play.enabled
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-500'
                            }`}>
                              {play.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleToggleGlobalPlay(play.id, play.enabled)}
                          disabled={togglingPlay === play.id}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            play.enabled ? 'bg-green-500' : 'bg-gray-300'
                          } ${togglingPlay === play.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              play.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4 italic">
                Note: When a play is disabled globally, User Admins will not see it in their Available Plays list and cannot enable it for their organization.
              </p>
            </div>
          </div>

          {/* Developer Tools */}
          <div className="bg-white rounded shadow border border-gray-100 mt-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-700">Developer Tools</h2>
              <p className="text-xs text-gray-500">Testing and development utilities</p>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 text-yellow-600 rounded-full">
                    <FaFlask size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Mock Test Data</p>
                    <p className="text-xs text-gray-500">
                      {testData.enabled
                        ? `Active: ${testData.test_sessions} sessions, ${testData.test_transactions} transactions`
                        : 'Generate fake login sessions and credit transactions for testing'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleToggleTestData}
                  disabled={togglingTestData}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    testData.enabled ? 'bg-yellow-500' : 'bg-gray-300'
                  } ${togglingTestData ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      testData.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* System Event Logs Download */}
          <div className="bg-white rounded shadow border border-gray-100 mt-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <FaDownload className="text-blue-500" /> System Event Logs
              </h2>
              <p className="text-xs text-gray-500">
                Download structured logs for AI/LLM-based diagnostics and troubleshooting
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Configuration */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 5, 7].map(days => (
                        <button
                          key={days}
                          onClick={() => setLogDays(days)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                            logDays === days
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {days === 1 ? 'Today' : `${days} days`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={logErrorsOnly}
                        onChange={e => setLogErrorsOnly(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                    </label>
                    <span className="text-sm text-gray-700">Errors only</span>
                  </div>

                  <button
                    onClick={handleDownloadLogs}
                    disabled={downloadingLogs}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingLogs ? (
                      <>
                        <span className="animate-spin">...</span> Generating...
                      </>
                    ) : (
                      <>
                        <FaDownload /> Download Logs (.txt)
                      </>
                    )}
                  </button>
                </div>

                {/* Stats Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Log Statistics</h4>
                  {logStats ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Period:</span>
                        <span className="font-medium">{logStats.period_days} day{logStats.period_days !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Events:</span>
                        <span className="font-medium">{logStats.total_events?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Operations:</span>
                        <span className="font-medium">{logStats.operations?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Info:</span>
                        <span className="font-medium text-blue-600">{logStats.by_level?.info?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Warnings:</span>
                        <span className="font-medium text-yellow-600">{logStats.by_level?.warn?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Errors:</span>
                        <span className={`font-medium ${logStats.by_level?.error > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {logStats.by_level?.error?.toLocaleString() || 0}
                        </span>
                      </div>

                      {logStats.top_errors && logStats.top_errors.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                            <FaExclamationTriangle className="text-red-400" /> Top Errors
                          </p>
                          <div className="space-y-1">
                            {logStats.top_errors.slice(0, 3).map((err, idx) => (
                              <div key={idx} className="text-xs text-gray-600 flex justify-between">
                                <span className="truncate flex-1 mr-2" title={err.message}>
                                  {err.message?.slice(0, 40)}...
                                </span>
                                <span className="text-red-500 font-medium">{err.count}x</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No log data available</p>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-4 italic">
                The downloaded log file is formatted for AI analysis. You can paste it into ChatGPT, Claude, or other LLMs to diagnose issues.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* User Admin Tab */}
      {activeTab === 'useradmin' && (
        <div>
          <UserAdminDashboard readOnly={false} />
        </div>
      )}

      {/* Credits Modal */}
      {showCreditModal && org && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className={`px-6 py-4 border-b border-gray-100 flex justify-between items-center ${creditMode === 'add' ? 'bg-green-50' : 'bg-red-50'}`}>
              <h3 className="text-lg font-bold text-gray-800">Manage Credits</h3>
              <button onClick={() => setShowCreditModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Mode Toggle */}
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                <button
                  onClick={() => setCreditMode('add')}
                  className={`flex-1 py-3 flex items-center justify-center gap-2 font-medium transition ${
                    creditMode === 'add'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FaArrowUp /> Add Credits
                </button>
                <button
                  onClick={() => setCreditMode('subtract')}
                  className={`flex-1 py-3 flex items-center justify-center gap-2 font-medium transition ${
                    creditMode === 'subtract'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FaArrowDown /> Remove Credits
                </button>
              </div>

              {/* Live Balance Preview */}
              <div className={`p-4 rounded-lg ${creditMode === 'add' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Current Balance</p>
                    <p className="text-2xl font-bold text-gray-700">{(org.credit_balance || 0).toLocaleString()}</p>
                  </div>
                  <div className={`px-3 ${creditMode === 'add' ? 'text-green-500' : 'text-red-500'}`}>
                    <FaArrowRight size={24} />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">New Balance</p>
                    <p className={`text-2xl font-bold ${
                      creditAmount
                        ? (creditMode === 'add'
                            ? 'text-green-600'
                            : ((org.credit_balance || 0) - Math.abs(parseInt(creditAmount) || 0) < 0 ? 'text-red-600' : 'text-orange-500'))
                        : 'text-gray-400'
                    }`}>
                      {creditAmount
                        ? (creditMode === 'add'
                            ? ((org.credit_balance || 0) + Math.abs(parseInt(creditAmount) || 0)).toLocaleString()
                            : ((org.credit_balance || 0) - Math.abs(parseInt(creditAmount) || 0)).toLocaleString())
                        : '—'
                      }
                    </p>
                  </div>
                </div>
                {creditAmount && (
                  <div className={`mt-3 pt-3 border-t ${creditMode === 'add' ? 'border-green-200' : 'border-red-200'} text-center`}>
                    <span className={`text-sm font-medium ${creditMode === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                      {creditMode === 'add' ? '+' : '-'}{Math.abs(parseInt(creditAmount) || 0).toLocaleString()} credits
                    </span>
                  </div>
                )}
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount to {creditMode === 'add' ? 'Add' : 'Remove'}
                </label>
                <input
                  type="number"
                  min="0"
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value.replace('-', ''))}
                  placeholder="Enter amount"
                  className={`w-full px-3 py-2 border rounded-lg outline-none ${
                    creditMode === 'add'
                      ? 'focus:ring-2 focus:ring-green-500 focus:border-green-500'
                      : 'focus:ring-2 focus:ring-red-500 focus:border-red-500'
                  }`}
                />
              </div>

              {/* Quick Amount Buttons */}
              <div className="flex gap-2 flex-wrap">
                {[500, 1000, 2500, 5000, 10000].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setCreditAmount(amt.toString())}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                      creditMode === 'add'
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {amt.toLocaleString()}
                  </button>
                ))}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={creditDescription}
                  onChange={e => setCreditDescription(e.target.value)}
                  placeholder={creditMode === 'add' ? 'e.g., Monthly allocation' : 'e.g., Refund adjustment'}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-300 outline-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreditModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCredits}
                  disabled={!creditAmount || parseInt(creditAmount) <= 0}
                  className={`px-6 py-2 text-white rounded disabled:opacity-50 flex items-center gap-2 ${
                    creditMode === 'add'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  <FaCheck /> {creditMode === 'add' ? 'Add Credits' : 'Remove Credits'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Licenses Modal */}
      {showLicenseModal && org && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800">Pooled Licenses</h3>
              <button onClick={() => setShowLicenseModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Current Licenses: <span className="font-bold text-blue-600">{org.max_concurrent_logins || 5}</span>
                </p>
                <p className="text-xs text-gray-500">
                  This is the threshold for daily logins shown in the chart. There is no hard cap - this is used for tracking and reporting purposes.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Pooled Licenses</label>
                <input
                  type="number"
                  min="1"
                  value={licenseAmount}
                  onChange={e => setLicenseAmount(e.target.value)}
                  placeholder="e.g., 10"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="flex gap-2 pt-4">
                {[5, 10, 25, 50, 100].map(num => (
                  <button
                    key={num}
                    onClick={() => setLicenseAmount(num.toString())}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  >
                    {num}
                  </button>
                ))}
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button onClick={() => setShowLicenseModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                <button
                  onClick={handleUpdateLicenses}
                  disabled={!licenseAmount || parseInt(licenseAmount) < 1}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <FaCheck /> Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdminDashboard;
